import { createSelector } from "@reduxjs/toolkit";
import {
  Commitment,
  CurrencyEnumType,
} from "@ifgengineering/client-invest-sdk";
import { RootState } from "../rootReducer";
import { entitiesSelector } from "../entity/entity.selectors";
import { investorSelector } from "../investor/investor.selectors";

type CapacityOptionBase = {
  label: string;
  count: number;
  currencies: CurrencyEnumType[];
};

export type PortfolioStatementCapacityOption =
  | (CapacityOptionBase & { capacity: "PERSONAL"; capacityName: "Personal" })
  | (CapacityOptionBase & { capacity: "ISA"; capacityName: "ISA" })
  | (CapacityOptionBase & {
      capacity: "ENTITY";
      capacityName: "Entity";
      entityId: string;
    });

const getUniqueCurrencies = (commitments: Commitment[]): CurrencyEnumType[] => [
  ...new Set(commitments.map((c) => c.currency)),
];

const countUniqueDeals = (commitments: Commitment[]): number =>
  new Set(commitments.map((c) => c.dealId)).size;

export const commitmentsSelector = (state: RootState): Commitment[] =>
  state.commitment.data || [];
export const isLoadingCommitmentsSelector = (state: RootState): boolean =>
  state.commitment.loading;
export const neverLoadedCommitmentsSelector = (state: RootState): boolean =>
  state.commitment.neverLoaded;

export const commitmentSelector = (
  state: RootState,
  commitmentId: string
): Commitment | undefined => {
  return state.commitment.data.find((item) => item.id === commitmentId);
};
export const commitmentsByDealSelector = (
  state: RootState,
  dealId?: string
): Commitment[] => {
  if (!dealId) return [];
  return state.commitment.data.filter((item) => item.dealId === dealId);
};

export const commitmentFromDealSelector = (
  state: RootState,
  dealId?: string
): Commitment | undefined => {
  if (!dealId) return;
  return state.commitment.data.find((item) => item.dealId === dealId);
};

export const allCommitmentsFromDealSelector = (
  state: RootState,
  dealId?: string
): Commitment[] | undefined => {
  if (!dealId) return;
  return state.commitment.data.filter((item) => item.dealId === dealId);
};

export const getHasConfirmedPaymentSelector = (state: RootState): boolean => {
  return state.commitment.data.some(
    (x) => x.oneOffPaymentSet.paymentsConfirmed.length > 0
  );
};

export const portfolioDataSelector = (state: RootState) => {
  return (
    state.commitment.data.filter((commitment) => commitment.showOnPortfolio) ||
    []
  );
};

export const getPortfolioStatementCapacityOptionsSelector = createSelector(
  [commitmentsSelector, entitiesSelector, investorSelector],
  (commitments, entities, investor): PortfolioStatementCapacityOption[] => {
    if (!investor) return [];

    const options: PortfolioStatementCapacityOption[] = [];
    const investorName = `${investor.firstName} ${investor.lastName}`;

    const personal = commitments.filter(
      (c) => c.showOnPortfolio && !c.entityId && !c.ifIsaAccountId
    );
    if (personal.length > 0) {
      options.push({
        capacity: "PERSONAL",
        label: investorName,
        capacityName: "Personal",
        count: countUniqueDeals(personal),
        currencies: getUniqueCurrencies(personal),
      });
    }

    const isa = commitments.filter(
      (c) => c.showOnPortfolio && !c.entityId && !!c.ifIsaAccountId
    );
    if (isa.length > 0) {
      options.push({
        capacity: "ISA",
        label: investorName,
        capacityName: "ISA",
        count: countUniqueDeals(isa),
        currencies: getUniqueCurrencies(isa),
      });
    }

    for (const entity of entities) {
      if (!entity.id) continue;

      const entityCommitments = commitments.filter(
        (c) => c.showOnPortfolio && c.entityId === entity.id
      );
      if (entityCommitments.length === 0) continue;

      options.push({
        capacity: "ENTITY",
        entityId: entity.id,
        label: entity.name,
        capacityName: "Entity",
        count: countUniqueDeals(entityCommitments),
        currencies: getUniqueCurrencies(entityCommitments),
      });
    }

    return options;
  }
);
